/**
 * This component is in charge of characters rendering.
 * It starts from the state config stored in the local state, once the elements are rendered, it takes the uuid
 * and updates the global store with the character states by uuid, next update the local state config competes with
 * the global store character's states and the last one is used to update the rendered objects.
 * Putting it in other words => on-start: renders from config, on-update: renders from global store.
 */

import { Fragment, useCallback, useRef, useState } from 'react'
import { Object3D, Vector2 } from 'three'
import CharactersAnimations from '../../f.assets/animations/CharactersAnimations'
import { Mage } from '../../f.assets/Characters/components/Mage/Mage'
import SkeletorMinion from '../../f.assets/Characters/components/SkeletorMinion/SkeletorMinion'
import CharacterConfig from '../../f.assets/Characters/scripts/classes/CharacterConfig'
import { CHARACTER_STATES, CHARACTER_TYPES, CHARACTER_TYPES as CHAR_TYPES } from '../../f.assets/Characters/scripts/constants'
import { CARDINAL_POINT, lookAtCardinalPoint } from '../../f.assets/scripts/lookAtCardinalPoint'
import replaceStandardByToonMaterial from '../../f.assets/scripts/replaceStandardByToonMaterial'
import { startWithAnimation } from '../../f.assets/scripts/startWithAnimation'
import { useScripts } from '../../f.assets/scripts/useScripts'
import GameElement from '../../e.elements/GameElements/GameElement/GameElement'
import { coordsToPosition } from '../../e.elements/Grid/scripts/helpers/coordsPositionConverter'
import { useSceneStore } from '../../a.scenes/GameScene/store'
import useTruthyEffect from '../../utils/hooks/useTruthyEffect'
import { Model as StaffCommon } from '../../f.assets/models/StaffCommon'
import BasicCommander from '../../f.assets/Characters/components/BasicCommander/BasicCommander'
import { Minion as MinionComponent } from '../../e.elements/GameElements/components'
import { CharacterBaseState } from '../../c.managers/CharactersManager/types'
import { addElementToGrid } from '../../c.managers/TerrainManager/scripts/scripts'
import { buriedMinion } from '../../c.managers/CharactersManager/scripts'
import getGameElementByUUID from '../../e.elements/GameElements/helpers/getGameElementByUUID'
import Minion from '../../e.elements/GameElements/classes/Minion'
import { ActionAttackTo, ActionGoTo, ActionLoot } from '../../f.assets/Characters/scripts/classes/CharacterActions'
import Treasure from '../../e.elements/GameElements/classes/Treasure'
import onPointerDownHandler from '../../c.managers/TerrainManager/scripts/onPointerDownHandler'
import Lollipop, { LOLLIPOP_TYPES } from '../../f.assets/Objects/components/Lollipop/Lollipop'
import { SEL_STATE_BY_UUID, SEL_TEAM_BY_UUID } from '../../c.managers/CharactersManager/selectors'
import BasicMinion from '../../f.assets/Characters/components/BasicMinion/BasicMinion'
import { COMMANDERS } from '../../c.managers/LevelManager/store.d'

export const API = {
  getState: (uuid: string) => useSceneStore.getState().characters.data.characterStates[uuid] as CharacterBaseState,
  updateState: (uuid: string, partialState: Partial<CharacterBaseState>) => {
    useSceneStore.getState().characters.updateCharacterState(uuid, partialState)
  },
  getOwner: (uuid: string) => API.getState(uuid).owner,
  addGoToAction: (uuid: string, coords: Vector2) => {
    const minion = getGameElementByUUID(uuid) as Minion
    minion.setActions([new ActionGoTo({ coords, mandatory: true })])
    if (![CHARACTER_STATES.BURIED, CHARACTER_STATES.DEAD, CHARACTER_STATES.INACTIVE].includes(minion.state)) {
      API.updateState(minion.uuid, { state: CHARACTER_STATES.MOVING })
    }
  },
  addAttackToAction: (uuid: string, uuidTarget: string) => {
    const minion = getGameElementByUUID(uuid) as Minion
    minion.setActions([new ActionAttackTo({ uuid: uuidTarget, mandatory: true })])
  },
  addLootAction: (uuid: string, uuidTarget: string) => {
    const minion = getGameElementByUUID(uuid) as Minion
    const treasure = getGameElementByUUID(uuidTarget) as Treasure

    // minion.loot()
    if(!minion.isInvalidated) {
      minion.setActions([new ActionLoot({ mandatory: true, coords: treasure.coords })])
    }
  },
  selectCharacter: (uuid: string) => {
    useSceneStore.getState().characters.setSelectedCharacterUUID(uuid)
  },
  clearCharacterSelection: () => {
    useSceneStore.getState().characters.clearSelectedCharacterUUID()
  },
  isSelected: (id: string) => {
    return useSceneStore.getState().characters.data.selectedCharacterUUID === id
  },
}
const CharactersController = () => {
  // LOCAL STATES
  // local states to render elements for the first time, it is build from charactersConfig
  const [localCharacterStates, setLocalCharacterStates] = useState<Record<string, CharacterBaseState>>({})
  // mutable object to store temporary states by uuid
  const tempStatesByUUID = useRef<Record<string, CharacterBaseState>>({})

  // STATES
  // gets the elements states, are used when the have data
  const characterStates = useSceneStore(s => s.characters.data.characterStates)
  // config from whom to build states
  const charactersConfig = useSceneStore(s => s.characters.data.charactersConfig)
  // the origin of the grid
  const gridOrigin = useSceneStore(s => s.terrain.data.gridOrigin)

  // METHODS
  // sets the api
  // const setCharactersController = useSceneStore(s => s.setCharacters)
  // sets elements states once we have them by uuid
  const setCharacterStates = useSceneStore(s => s.characters.setCharacterStates)

  const API = {
    buildCharacterStates: (charactersConfig: CharacterConfig[]) => {
      const charStates = charactersConfig.reduce((states: Record<string, CharacterBaseState>, ch) => {
        if (ch.key !== undefined) {
          states[ch.key] = ch
        }
        // {
        //   position: ch.position,
        //   coords: ch.coords,
        //   type: ch.type,
        //   key: ch.key,
        //   mv: ch.mv,
        // }
        return states
      }, {})
      setLocalCharacterStates(charStates)
    },
  }

  // ON CONFIG
  useTruthyEffect(() => {
    // creates states from config
    API.buildCharacterStates(charactersConfig)
  }, [charactersConfig])

  const handleOnAddedToScene = useCallback(
    (state: CharacterBaseState) => {
      return (obj: Object3D) => {
        if (obj && !tempStatesByUUID.current?.hasOwnProperty(obj.uuid)) {
          state.position = obj.position

          tempStatesByUUID.current[obj.uuid] = state

          if (Object.keys(tempStatesByUUID.current).length === Object.keys(localCharacterStates).length) {
            // all elements for states are rendered
            setCharacterStates(tempStatesByUUID.current)
          }
        }
      }
    },
    [localCharacterStates],
  )

  // first render we use the local states, after that, we use the global state
  const states: Record<string, CharacterBaseState> =
    Object.keys(characterStates).length > 0 ? characterStates : localCharacterStates

  return (
    <>
      {Object.keys(states).map(sKey => {
        const state = states[sKey]
        const position = coordsToPosition(state.coords, gridOrigin)

        switch (state.type) {
          case CHAR_TYPES.BASIC_MINION:
            const team = state.weapon ? SEL_TEAM_BY_UUID(sKey)(useSceneStore.getState()) : ''            
            
            return (
              // <Fragment key={`Fragment_${state.key}`}>
              <MinionComponent
                key={state.key}
                addedToScene={handleOnAddedToScene(state)}
                userData={{
                  coords: state.coords,
                  tickDivisions: state.tickDivisions,
                  type: CHAR_TYPES.BASIC_MINION,
                  config: state,
                }}
                // onStart={useScripts([replaceStandardByToonMaterial,lookAtCardinalPoint(CARDINAL_POINT.SOUTHWEST), startWithAnimation('Idle')]) }
                onStart={useScripts([addElementToGrid, buriedMinion/*, replaceStandardByToonMaterial*/])}
                // onPointerDown={onPointerDownHandler}
                onClick={onPointerDownHandler}
              >
                <CharactersAnimations>
                  <SkeletorMinion
                    rightArm={
                      state.weapon && (
                        <Lollipop candyType={team === CHARACTER_TYPES.JACK ? LOLLIPOP_TYPES.LOLLIPOPB : LOLLIPOP_TYPES.LOLLIPOPA} />
                      )
                    }
                  />
                </CharactersAnimations>
                {/* <BasicMinion color={state.owner === COMMANDERS.COM ? 0xaa0000 : 0x00aa00} /> */}
              </MinionComponent>
              // </Fragment>
            )
          case CHAR_TYPES.BASIC_COMMANDER:
            return (
              <GameElement
                key={state.key}
                // addedToScene={handleOnAddedToScene(state)}
                position={position}
                userData={{ coords: state.coords, type: CHAR_TYPES.BASIC_COMMANDER }}
                onStart={addElementToGrid}
                // onStart={useScripts([replaceStandardByToonMaterial,lookAtCardinalPoint(CARDINAL_POINT.SOUTHWEST), startWithAnimation('Idle')]) }
              >
                <BasicCommander />
              </GameElement>
            )
          case CHAR_TYPES.SKELETOR_MINION:
            return (
              <GameElement
                key={state.key}
                addedToScene={handleOnAddedToScene(state)}
                position={position}
                userData={{ coords: state.coords }}
                onStart={useScripts([
                  replaceStandardByToonMaterial,
                  lookAtCardinalPoint(CARDINAL_POINT.SOUTHWEST),
                  startWithAnimation('Idle'),
                ])}
              >
                <CharactersAnimations>
                  <SkeletorMinion />
                </CharactersAnimations>
              </GameElement>
            )
          case CHAR_TYPES.MAGE:
            return (
              <GameElement
                key={state.key}
                addedToScene={handleOnAddedToScene(state)}
                position={position}
                userData={{ coords: state.coords }}
                onStart={useScripts([
                  replaceStandardByToonMaterial,
                  lookAtCardinalPoint(CARDINAL_POINT.SOUTHEAST),
                  startWithAnimation('Idle'),
                ])}
              >
                <CharactersAnimations>
                  <Mage rightArm={<StaffCommon />} />
                </CharactersAnimations>
              </GameElement>
            )
        }
      })}
    </>
  )
}

export default CharactersController
